import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Lightbulb, Search, User, Building2, X } from "lucide-react";
import sh from "bundles/common/utils/sh";

const labelSx = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "hsl(150 10% 46%)",
  mb: 0.5,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
};

const nativeInputSx = {
  width: "100%",
  height: 40,
  borderRadius: "6px",
  border: "1px solid hsl(140 8% 88%)",
  backgroundColor: "hsl(140 10% 95%)",
  px: 1.5,
  py: 1,
  fontSize: 14,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  outline: "none",
  boxSizing: "border-box",
  "&::placeholder": { color: "hsl(150 10% 46%)" },
  "&:focus": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: "2px" },
};

const CreateSuggestionModal = ({ open, onClose, onSubmit, submitting }) => {
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidateResults, setCandidateResults] = useState([]);
  const [searchingCandidates, setSearchingCandidates] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const [companySearch, setCompanySearch] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    if (!open) {
      setCandidateSearch("");
      setCandidateResults([]);
      setSelectedCandidate(null);
      setCompanySearch("");
      setCompanyResults([]);
      setSelectedCompany(null);
    }
  }, [open]);

  const canSubmit = useMemo(() => {
    return selectedCandidate !== null && selectedCompany !== null;
  }, [selectedCandidate, selectedCompany]);

  const handleCandidateSearch = useCallback((val) => {
    setCandidateSearch(val);
    const q = val.trim();
    if (q.length < 2) { setCandidateResults([]); return; }
    setSearchingCandidates(true);
    sh.get("/internal/search/candidates", { params: { q } })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCandidateResults(data.map((c) => ({
          ...c,
          full_name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
          display_label: `${`${c.first_name || ""} ${c.last_name || ""}`.trim()} - ${c.email || ""}`,
        })));
      })
      .catch(() => setCandidateResults([]))
      .finally(() => setSearchingCandidates(false));
  }, []);

  const handleCompanySearch = useCallback((val) => {
    setCompanySearch(val);
    const q = val.trim();
    if (q.length < 2) { setCompanyResults([]); return; }
    setSearchingCompanies(true);
    sh.get("/internal/search/companies", { params: { q } })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCompanyResults(data);
      })
      .catch(() => setCompanyResults([]))
      .finally(() => setSearchingCompanies(false));
  }, []);

  const handleClose = () => onClose?.();

  const handleSubmit = () => {
    if (!canSubmit) return;
    const payload = {
      candidate_id: selectedCandidate.id,
      company_id: selectedCompany.id,
      selected_by: "Talent team",
    };
    onSubmit?.(payload, {
      reset: () => {
        setCandidateSearch("");
        setCandidateResults([]);
        setSelectedCandidate(null);
        setCompanySearch("");
        setCompanyResults([]);
        setSelectedCompany(null);
      },
    });
  };

  const SectionHeader = ({ icon: Icon, title }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 0.5 }}>
      <Box
        sx={{
          backgroundColor: "hsl(150 38% 17%)",
          borderRadius: "6px",
          p: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={14} color="hsl(80 97% 72%)" />
      </Box>
      <Typography
        sx={{
          fontWeight: 600,
          color: "hsl(150 38% 17%)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  const renderSearchField = ({
    label,
    placeholder,
    searchValue,
    onSearch,
    searching,
    results,
    selected,
    onSelect,
    onClear,
    displayKey,
    secondaryKey,
  }) => (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      {selected ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            height: 40,
            borderRadius: "6px",
            border: "1px solid hsl(150 38% 17%)",
            backgroundColor: "hsla(150, 38%, 17%, 0.05)",
            boxSizing: "border-box",
          }}
        >
          <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", flex: 1 }}>
            {selected[displayKey]}{secondaryKey && selected[secondaryKey] ? ` — ${selected[secondaryKey]}` : ""}
          </Typography>
          <Button
            size="small"
            disableRipple
            onClick={onClear}
            sx={{ textTransform: "none", fontSize: 12, color: "hsl(0 72% 51%)", minWidth: "auto", p: 0, "&:hover": { backgroundColor: "transparent" } }}
          >
            Clear
          </Button>
        </Box>
      ) : (
        <>
          <Box sx={{ position: "relative" }}>
            <Box
              component="input"
              placeholder={placeholder}
              value={searchValue}
              onChange={(e) => onSearch(e.target.value)}
              sx={nativeInputSx}
            />
            <Box sx={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", display: "flex" }}>
              {searching ? <CircularProgress size={14} sx={{ color: "hsl(150 38% 17%)" }} /> : <Search size={14} color="hsl(150 10% 46%)" />}
            </Box>
          </Box>
          {!searching && searchValue.trim().length >= 2 && results.length === 0 && (
            <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", textAlign: "center", py: 1 }}>No results found</Typography>
          )}
          {results.length > 0 && (
            <Paper elevation={0} sx={{ mt: 0.5, maxHeight: 180, overflow: "auto", border: "1px solid hsl(140 8% 88%)", borderRadius: "6px" }}>
              <List disablePadding dense>
                {results.map((item) => (
                  <ListItemButton
                    key={item.id}
                    onClick={() => onSelect(item)}
                    sx={{ py: 0.5, "&:hover": { backgroundColor: "hsl(140 10% 95%)" } }}
                  >
                    <ListItemText
                      primary={item[displayKey]}
                      primaryTypographyProps={{ sx: { fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" } }}
                      secondary={secondaryKey ? item[secondaryKey] : undefined}
                      secondaryTypographyProps={{ sx: { fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui" } }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: 2.5,
          py: 2.5,
          backgroundColor: "hsl(150 38% 17%)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              backgroundColor: "hsla(80, 97%, 72%, 0.15)",
              borderRadius: "10px",
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Lightbulb size={20} color="#fff" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#fff" }}>
              Create new suggestion
            </Typography>
            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsla(80, 97%, 72%, 0.7)" }}>
              Suggest a candidate to a company
            </Typography>
          </Box>
        </Stack>
        <IconButton
          disableRipple
          size="small"
          onClick={handleClose}
          sx={{ p: 0.5, borderRadius: "6px", color: "hsla(80, 97%, 72%, 0.7)", "&:hover": { backgroundColor: "hsla(255,255,255,0.1)" } }}
        >
          <X size={18} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5, backgroundColor: "#fff" }}>
        <Stack spacing={2}>
          <SectionHeader icon={User} title="Candidate" />
          {renderSearchField({
            label: "Candidate *",
            placeholder: "Search by name or email...",
            searchValue: candidateSearch,
            onSearch: handleCandidateSearch,
            searching: searchingCandidates,
            results: candidateResults,
            selected: selectedCandidate,
            onSelect: (item) => {
              setSelectedCandidate({ id: item.id, full_name: item.full_name, email: item.email, display_label: item.display_label });
              setCandidateSearch("");
              setCandidateResults([]);
            },
            onClear: () => setSelectedCandidate(null),
            displayKey: "display_label",
          })}

          <Box sx={{ borderTop: "1px solid hsl(140 8% 88%)", pt: 2 }} />
          <SectionHeader icon={Building2} title="Company" />
          {renderSearchField({
            label: "Company *",
            placeholder: "Search by company name...",
            searchValue: companySearch,
            onSearch: handleCompanySearch,
            searching: searchingCompanies,
            results: companyResults,
            selected: selectedCompany,
            onSelect: (item) => {
              setSelectedCompany({ id: item.id, name: item.name });
              setCompanySearch("");
              setCompanyResults([]);
            },
            onClear: () => setSelectedCompany(null),
            displayKey: "name",
          })}

        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          borderTop: "1px solid hsl(140 8% 88%)",
          backgroundColor: "#fff",
        }}
      >
        <Button
          disableRipple
          onClick={handleClose}
          disabled={submitting}
          sx={{
            textTransform: "none",
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(150 10% 46%)",
            borderRadius: "8px",
            px: 2,
            "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
          }}
        >
          Cancel
        </Button>
        <Button
          disableRipple
          disableElevation
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          sx={{
            textTransform: "none",
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            borderRadius: "8px",
            px: 3,
            py: 1,
            minHeight: 0,
            height: "auto",
            backgroundColor: "hsl(150 38% 17%)",
            color: "hsl(80 97% 72%)",
            transition: "opacity 0.15s ease",
            "&:hover": {
              backgroundColor: "hsl(150 38% 17%)",
              opacity: 0.9,
            },
            "&.Mui-disabled": {
              opacity: 0.5,
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              pointerEvents: "none",
            },
          }}
        >
          {submitting ? (
            <CircularProgress size={18} sx={{ color: "hsl(80 97% 72%)", mr: 1 }} />
          ) : null}
          {submitting ? "Creating..." : "Create suggestion"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSuggestionModal;
